import { isNativeStorageDisabled } from "./native-runtime";
import { isWeb } from "./platform";
import { safeJsonParse } from "./safe-json";
import { loadSqliteKeyValueStore } from "./sqlite-kv-store";

type StorageClient = {
	set: (key: string, value: string) => void;
	getString: (key: string) => string | undefined;
	delete: (key: string) => void;
	clearAll: () => void;
};

const createMemoryStorage = (): StorageClient => {
	const storageMap = new Map<string, string>();

	return {
		set: (key, value) => {
			storageMap.set(key, value);
		},
		getString: (key) => storageMap.get(key),
		delete: (key) => {
			storageMap.delete(key);
		},
		clearAll: () => {
			storageMap.clear();
		},
	};
};

const createWebStorage = (): StorageClient => {
	if (typeof localStorage === "undefined") {
		return createMemoryStorage();
	}

	return {
		set: (key, value) => {
			localStorage.setItem(key, value);
		},
		getString: (key) => localStorage.getItem(key) ?? undefined,
		delete: (key) => {
			localStorage.removeItem(key);
		},
		clearAll: () => {
			localStorage.clear();
		},
	};
};

/**
 * Expo Go cannot load MMKV (a JSI module), so persist through the SQLite-backed
 * key-value store bundled with Expo Go instead of losing state on every reload.
 */
const createExpoGoStorage = (): StorageClient => {
	const store = loadSqliteKeyValueStore();
	if (!store) {
		return createMemoryStorage();
	}

	return {
		set: (key, value) => {
			store.setItemSync(key, value);
		},
		getString: (key) => store.getItemSync(key) ?? undefined,
		delete: (key) => {
			store.removeItemSync(key);
		},
		clearAll: () => {
			store.clearSync();
		},
	};
};

const createStorage = (): StorageClient => {
	if (isWeb) {
		return createWebStorage();
	}

	if (isNativeStorageDisabled()) {
		return createExpoGoStorage();
	}

	try {
		const module = require("react-native-mmkv") as {
			MMKV?: new () => StorageClient;
		};
		if (!module.MMKV) {
			return createMemoryStorage();
		}
		return new module.MMKV();
	} catch (error) {
		if ((globalThis as { __DEV__?: boolean }).__DEV__) {
			console.warn(
				"[storage] MMKV unavailable, falling back to in-memory storage.",
				error,
			);
		}
		return createMemoryStorage();
	}
};

export const storage = createStorage();

const keys = {
	isDarkMode: "isDarkMode",
	themePreference: "themePreference",
};

const DEVICE_SCOPED_KEYS: (keyof typeof keys)[] = [
	"isDarkMode",
	"themePreference",
];

/**
 * Wipes user-scoped storage (sign-out, app switch) while preserving
 * device-scoped preferences.
 */
export const clearUserScopedStorage = () => {
	const preserved = DEVICE_SCOPED_KEYS.map(
		(key) => [key, storage.getString(key)] as const,
	);

	storage.clearAll();

	for (const [key, value] of preserved) {
		if (value !== undefined) {
			storage.set(key, value);
		}
	}
};

export const storeKey = (key: keyof typeof keys, value: unknown) => {
	storage.set(key, JSON.stringify(value || ""));
};

export const deleteKey = (key: keyof typeof keys) => {
	storage.delete(key);
};

export const retrieveKey = (key: keyof typeof keys) => {
	const result = storage.getString(key);
	if (result === undefined) {
		return null;
	}

	return safeJsonParse(result);
};
